import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { KineticReveal } from "@/components/motion/Reveal";
import { ASSETS, BRAND } from "@/lib/site";
import { ArrowDown } from "lucide-react";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const mockupY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const blobY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const blob2Y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] overflow-hidden bg-brand-gradient text-white"
      data-testid="hero"
    >
      {/* Decorative parallax blobs */}
      <motion.div
        style={{ y: blobY }}
        className="pointer-events-none absolute -top-40 -right-24 h-[36rem] w-[36rem] rounded-full bg-white/15 blur-3xl"
      />
      <motion.div
        style={{ y: blob2Y }}
        className="pointer-events-none absolute top-1/3 -left-40 h-[28rem] w-[28rem] rounded-full bg-[#FF1E5A]/40 blur-3xl"
      />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 min-h-[100svh] grid lg:grid-cols-2 gap-14 items-center pt-28 pb-16">
        {/* Left: copy */}
        <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="text-xs uppercase tracking-[0.28em] font-semibold text-white/85 mb-7"
            data-testid="hero-overline"
          >
            Vote-Estimate &nbsp;·&nbsp; Built for the Indigenous community
          </motion.p>

          <h1 className="font-serif font-semibold tracking-tight leading-[0.9] text-[15vw] sm:text-6xl lg:text-7xl xl:text-8xl">
            <KineticReveal lines={["The smart way"]} delay={0.5} />
            <KineticReveal lines={["to run a vote."]} delay={0.66} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8 }}
            className="mt-8 max-w-md text-base md:text-lg text-white/90 leading-relaxed"
            data-testid="hero-subtitle"
          >
            SmartBallot helps Indigenous Communities plan, cost, and run their voting events
            with confidence. Build a polished, budget-ready proposal in minutes
            with Vote-Estimate.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#vote-estimate"
              data-testid="hero-primary-cta"
              className="px-7 py-3.5 rounded-full bg-white text-[#FF2E63] font-semibold hover:bg-white/90 transition-colors duration-300 shadow-lg shadow-black/10"
            >
              Explore Vote-Estimate
            </a>
            <a
              href="#contact"
              data-testid="hero-secondary-cta"
              className="px-7 py-3.5 rounded-full border border-white/60 text-white hover:bg-white/10 transition-colors duration-300"
            >
              Let’s Talk
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 1 }}
            className="mt-16 flex items-center gap-3 text-white/70 text-xs uppercase tracking-[0.2em]"
          >
            <ArrowDown size={14} className="animate-bounce" />
            {BRAND.domain}
          </motion.div>
        </div>

        {/* Right: product mockup */}
        <motion.div
          style={{ y: mockupY }}
          initial={{ opacity: 0, y: 40, rotate: -3 }}
          animate={{ opacity: 1, y: 0, rotate: -2 }}
          transition={{ delay: 0.8, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden lg:block will-change-transform"
        >
          <div className="absolute -inset-8 bg-white/25 blur-3xl rounded-[3rem]" />
          <div className="relative rounded-2xl overflow-hidden border border-white/40 shadow-2xl shadow-black/30">
            <img src={ASSETS.heroShot} alt="Vote-Estimate in use — a real Chief & Council election proposal" className="w-full" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
